import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { UserListUpdate } from "./update";
import { UserListAdd } from "./add";
import {
  setSearchUserTerm,
  getAllUserList,
  setSearchUserLoading,
  setPage,
  getUserAccessAndLanguages,
  getRoles,
  getUserDetails,
  reset,
  resetUserList,
  setUpdateBoxOpenForUser,
} from "../../../redux/slices/sellerDataUserListSlice";
import {
  Box,
  CircularProgress,
  Grid,
  useMediaQuery,
  useTheme,
  IconButton,
  ClickAwayListener,
} from "@mui/material";

import {
  UserContainer,
  UserListContainer,
  UserBox,
  UserDetailsBox,
  UserName,
  UserType,
  UserAccess,
  UserAccessDetailsBox,
  UserEditBox,
  UserEditIcon,
  UserAccessCheckIcon,
  UserAccessSubBox,
  SearchInput,
  AddUserBox,
  MainContainer,
  SearchBox,
  SearchButton,
  ButtonBox,
  UserPendingIcon,
  NoUserBox,
  NoUserMessage,
  SearchLoadingIcon,
  LoadingBox,
} from "./styles/index";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { ROLES } from "../../../utils/constants";

export const UserList = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);

  const { state, sellerData, msal } = useSelector((state) => ({
    state: state.sellerDataUserList,
    sellerData: state.seller.selectedSeller,
    msal: state.seller.msal,
  }));

  useEffect(() => {
    dispatch(getUserAccessAndLanguages());
    dispatch(getRoles());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        setContainerWidth(width);
      }
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    dispatch(setSearchUserTerm(""));
    dispatch(reset());
    fetchUsers();

    // eslint-disable-next-line
  }, [sellerData?.seller_id]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [state.page]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(resetUserList());
    fetchUsers();

    // eslint-disable-next-line
  }, [state.toggle]);

  const fetchUsers = (searchTerm = true) => {
    if (sellerData) {
      const params = { offset: state.offset };
      if (state.searchUserTerm.trim() && searchTerm)
        params.searchTerm = state.searchUserTerm;

      dispatch(
        getAllUserList({
          seller_id: sellerData.seller_id,
          params: params,
        })
      );
    }
  };

  const handleOnChangeSearch = (e) => {
    const { value } = e.target;
    dispatch(setSearchUserTerm(value));

    if (!value) {
      handleSearchClear();
    }
  };

  const handleSearchClear = (e) => {
    dispatch(setSearchUserTerm(""));
    dispatch(resetUserList());
    fetchUsers(false);
  };

  const handleOnClickSearch = (e) => {
    dispatch(setSearchUserLoading());
    dispatch(resetUserList());
    fetchUsers();
  };

  const handleKeyPressSearch = (e) => {
    if (e.key === "Enter") {
      handleOnClickSearch();
    }
  };

  const fetchUsersDataOnScroll = () => {
    dispatch(setPage());
  };

  const isMobile = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "md")
  );

  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("md", "lg")
  );

  const [sentinelRef] = useInfiniteScroll({
    loading: state.loading,
    hasNextPage: state.hasMoreUsers,
    onLoadMore: fetchUsersDataOnScroll,
    rootMargin: "0px 0px 10px 0px",
  });

  const handleUpdatePopperwidth = () => {
    if (isTablet) return containerWidth - (containerWidth / 100) * 15;

    const value = (containerWidth / 100) * 25;
    return containerWidth - value;
  };

  const handleOnClickEdit = (index, userId) => {
    dispatch(getUserDetails({ user_id: userId, index: index }));
  };

  const handleClickAway = () => {
    dispatch(setUpdateBoxOpenForUser(state.open));
  };

  return (
    <>
      <MainContainer
        container
        theme={theme}
        
        sx={{ overflow: "auto",maxWidth:'1200px' }}
      >
        <AddUserBox item>
          {msal.roles.some(
            (role) =>
              role === ROLES.SUPER_ADMIN || role === ROLES.BUSINESS_ADMIN
          ) ? (
            <UserListAdd
              isMobile={isMobile}
              isTablet={isTablet}
              sellerId={sellerData?.seller_id}
            ></UserListAdd>
          ) : null}
        </AddUserBox>
        <UserListContainer
          item
          xs={12}
          sm={isMobile ? 12 : 5.9}
          ref={containerRef}
        >
          <SearchBox container>
            <Grid item xs={12} sm={isMobile ? 12 : 9}>
              <SearchInput
                id="standard-basic"
                label="Search for User"
                variant="standard"
                name="searchUserTerm"
                value={state.searchUserTerm}
                onChange={handleOnChangeSearch}
                onKeyPress={handleKeyPressSearch}
                InputProps={{
                  endAdornment: state.searchUserTerm && (
                    <ButtonBox onClick={handleSearchClear}>
                      <ClearIcon />
                    </ButtonBox>
                  ),
                }}
              />
            </Grid>
            <Grid item sm={isMobile ? 0 : 0.5} />
            {state.searchUserTerm.length ? (
              <Grid
                item
                xs={12}
                sm={isMobile ? 12 : 2.5}
                onClick={handleOnClickSearch}
              >
                <SearchButton variant="contained" size="large">
                  {state.searchUserLoading ? (
                    <SearchLoadingIcon size={25} />
                  ) : (
                    <SearchIcon />
                  )}
                </SearchButton>
              </Grid>
            ) : (
              <></>
            )}
          </SearchBox>
        </UserListContainer>
        <UserContainer item xs={12} sm={isMobile ? 12 : 5.9} ref={containerRef}>
          {state.users.length ? (
            state.users.map((user, index) => (
              <Grid
                container
                sx={{
                  position: "relative",
                  ...(isMobile && {
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    boxSizing: "border-box",
                  }),
                }}
                key={user.user_id}
              >
                <UserBox item open={state.open} index={index}>
                  {/* <span>{index}</span> */}
                  <UserDetailsBox item xs={7.8}>
                    <UserName>{`${user.user_first_name} ${user.user_last_name}`}</UserName>
                    <UserType>
                      {user.role_en}
                      {user.sub_role_type_id && state.subRoles?.length
                        ? ` - ${
                            state.subRoles.find(
                              (subRole) =>
                                subRole?.sub_role_type_id ===
                                user.sub_role_type_id
                            )?.sub_role_type_en
                          }`
                        : null}
                    </UserType>
                  </UserDetailsBox>
                  <UserAccessDetailsBox item xs={3.2}>
                    {user.user_approved ? (
                      user.additional_access ? (
                        user.additional_access
                          .split("|")
                          .map((add_access, i) => {
                            if (!add_access) return null;

                            let additional_access_id = "";
                            let additional_access_en = "";

                            add_access.split(",").map((e) => {
                              if (e) {
                                const [key, value] = e.split(":");

                                if (key === `additional_access_id`)
                                  additional_access_id = value;

                                if (key === `additional_access_en`)
                                  additional_access_en = value;
                              }

                              return null;
                            });

                            return (
                              <UserAccessSubBox key={additional_access_id}>
                                <UserAccessCheckIcon />
                                <UserAccess>{additional_access_en}</UserAccess>
                              </UserAccessSubBox>
                            );
                          })
                      ) : (
                        <></>
                      )
                    ) : (
                      <UserAccessSubBox>
                        <UserPendingIcon />
                        <UserAccess>Pending</UserAccess>
                      </UserAccessSubBox>
                    )}
                  </UserAccessDetailsBox>
                  <UserEditBox item xs={1}>
                    {msal.roles.some(
                      (role) =>
                        role === ROLES.SUPER_ADMIN ||
                        role === ROLES.BUSINESS_ADMIN
                    ) ? (
                      <IconButton
                        onClick={() => handleOnClickEdit(index, user.user_id)}
                      >
                        <UserEditIcon index={index} open={state.open} />
                      </IconButton>
                    ) : null}
                  </UserEditBox>
                </UserBox>
                {state.open === index ? (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <div>
                      <UserListUpdate
                        width={isMobile ? "100%" : handleUpdatePopperwidth()}
                        open={state.open}
                        userId={user.user_id}
                        isMobile={isMobile}
                        index={index}
                        sellerId={sellerData?.seller_id}
                      />
                    </div>
                  </ClickAwayListener>
                ) : null}
              </Grid>
            ))
          ) : !state.searchUserLoading &&
            state.searchUserTerm &&
            state.searchUserTerm.trim() ? (
            <NoUserBox item>
              <NoUserMessage>
                Sorry, we couldn’t find any matches for this user
              </NoUserMessage>
            </NoUserBox>
          ) : null}
          {state.loading && !state.searchUserLoading ? (
            <LoadingBox item>
              <CircularProgress></CircularProgress>
            </LoadingBox>
          ) : null}
          <Box ref={sentinelRef}></Box>
        </UserContainer>

        <Box
          xs={5}
          // height={isMobile ? "0px" : `${state.userEditBoxHeight-160}px`}
        ></Box>
      </MainContainer>
    </>
  );
};
